import { useMutation, useQuery } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { gql } from "~/__generated__"
import { createPasskey, passkeysSupported } from "~/auth/passkeys"
import { formatDate } from "~/common/date"
import { useFormErrorHandling } from "~/common/error-handling"
import { profilePath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { PageLayout } from "~/layouts/page-layout"
import { Button } from "~/ui/button"
import { Form } from "~/ui/form"
import { TextField } from "~/ui/forms/fields/text-field"
import { FormGeneralErrors } from "~/ui/forms/form-general-errors"
import { GraphqlError } from "~/ui/graphql-error"
import { LinkButton } from "~/ui/link-button"
import { Section, SectionLabel } from "~/ui/section"

const SECURITY_QUERY = gql(/* GraphQL */ `
  query SecurityScreen {
    viewer {
      id
      passkeys {
        id
        nickname
        createdAt
        lastUsedAt
      }
    }
  }
`)

const REGISTRATION_OPTIONS_MUTATION = gql(/* GraphQL */ `
  mutation PasskeyRegistrationOptions {
    passkeyRegistrationOptions(input: {}) {
      optionsJson
    }
  }
`)

const REGISTER_MUTATION = gql(/* GraphQL */ `
  mutation PasskeyRegister($input: PasskeyRegisterInput!) {
    passkeyRegister(input: $input) {
      passkey {
        id
      }
    }
  }
`)

const DELETE_MUTATION = gql(/* GraphQL */ `
  mutation PasskeyDelete($input: PasskeyDeleteInput!) {
    passkeyDelete(input: $input) {
      success
    }
  }
`)

const PASSWORD_CHANGE_MUTATION = gql(/* GraphQL */ `
  mutation PasswordChange($input: PasswordChangeInput!) {
    passwordChange(input: $input) {
      success
    }
  }
`)

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, { message: "Enter your current password" }),
    newPassword: z.string().min(12, { message: "Use at least 12 characters" }),
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })

type PasswordValues = z.infer<typeof passwordSchema>

const PasskeysSection = () => {
  const { data, error, refetch } = useQuery(SECURITY_QUERY)
  const [registrationOptions] = useMutation(REGISTRATION_OPTIONS_MUTATION)
  const [register] = useMutation(REGISTER_MUTATION)
  const [remove] = useMutation(DELETE_MUTATION)
  const [nickname, setNickname] = React.useState("1Password")
  const [adding, setAdding] = React.useState(false)

  const addPasskey = async () => {
    setAdding(true)
    try {
      const options = await registrationOptions()
      if (options.error || !options.data) throw options.error
      const credentialJson = await createPasskey(
        options.data.passkeyRegistrationOptions.optionsJson
      )
      const result = await register({ variables: { input: { credentialJson, nickname } } })
      if (result.error) throw result.error
      await refetch()
      toast.success("Passkey added")
    } catch (caught) {
      toast.error("Passkey not added", {
        description: caught instanceof Error ? caught.message : "Please try again.",
      })
    } finally {
      setAdding(false)
    }
  }

  const removePasskey = async (id: string) => {
    if (!window.confirm("Remove this passkey?")) return
    const result = await remove({ variables: { input: { id } } })
    if (result.error) {
      toast.error("Passkey not removed", { description: result.error.message })
      return
    }
    await refetch()
    toast.success("Passkey removed")
  }

  const passkeys = data?.viewer?.passkeys ?? []

  return (
    <Section variant="bordered">
      <SectionLabel>Passkeys</SectionLabel>
      {error && <GraphqlError error={error} />}
      {passkeys.length === 0 ? (
        <p className="mb-4 text-sm text-muted-foreground">No passkeys yet.</p>
      ) : (
        <ul className="mb-4 divide-y">
          {passkeys.map((passkey) => (
            <li key={passkey.id} className="flex items-center justify-between gap-4 py-3">
              <div>
                <div className="font-medium">{passkey.nickname}</div>
                <div className="text-sm text-muted-foreground">
                  Added {formatDate(passkey.createdAt)}
                  {passkey.lastUsedAt && ` · Last used ${formatDate(passkey.lastUsedAt)}`}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => removePasskey(passkey.id)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      {passkeysSupported() ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-400">Passkey name</span>
            <input
              className="h-10 rounded-md border border-input bg-background px-3"
              value={nickname}
              onChange={(event) => setNickname(event.target.value)}
            />
          </label>
          <Button onClick={addPasskey} disabled={adding}>
            {adding ? "Waiting for passkey..." : "Add a passkey"}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">This browser does not support passkeys.</p>
      )}
    </Section>
  )
}

const PasswordSection = () => {
  const [changePassword, { loading }] = useMutation(PASSWORD_CHANGE_MUTATION)
  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  })
  const { onError } = useFormErrorHandling(form)

  const onSubmit = async (values: PasswordValues) => {
    const { error } = await changePassword({
      onError,
      variables: {
        input: { currentPassword: values.currentPassword, newPassword: values.newPassword },
      },
    })
    if (error) return
    form.reset()
    toast.success("Password changed")
  }

  return (
    <Section variant="bordered">
      <SectionLabel>Password</SectionLabel>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-md space-y-4">
          <FormGeneralErrors control={form.control} />
          <TextField
            control={form.control}
            name="currentPassword"
            label="Current password"
            type="password"
            autoComplete="current-password"
          />
          <TextField
            control={form.control}
            name="newPassword"
            label="New password"
            type="password"
            autoComplete="new-password"
          />
          <TextField
            control={form.control}
            name="confirmPassword"
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
          />
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Change password"}
          </Button>
        </form>
      </Form>
    </Section>
  )
}

export const SecurityScreen = () => {
  useDocumentTitle("Sign-in & security")

  return (
    <PageLayout
      title="Sign-in & security"
      actions={<LinkButton to={profilePath({})}>Back to my account</LinkButton>}
    >
      <PasskeysSection />
      <PasswordSection />
    </PageLayout>
  )
}
